import Ticket from "../../interfaces/ticket/Ticket";
import { NewTicketRequest } from "../../interfaces/ticket/NewTicketRequest";
import { User } from "../../interfaces/User";
import Tag from "../../interfaces/tag/Tag";
import { ReorderRequest } from "../../interfaces/ticket/ReorderRequest";
import { SubmitBugRequest } from "../../interfaces/ticket/SubmitBugRequest";

import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { apiPost } from "../../utilities/api-client/apiRequest";
import { planningRequest } from './planningRequest';

/**
 * Responses that the API contract defines as carrying the viewer's reply state
 * (list, collection, detail, reply dismissal). The API omits null properties, so
 * an absent value there means "no reply state" rather than "not provided".
 */
export const withProvidedReplyState = <T extends Ticket | null | undefined>(ticket: T): T =>
    ticket && ticket.replyState === undefined ? { ...ticket, replyState: null } : ticket;

// Function to fetch a ticket by its ID
export async function getTicketById(
    ticketId: string,
    authToken: string,
    includeComments: boolean = false,
    includeAttachmentUrls: boolean = false
): Promise<Ticket | null> {
    const queryParams = new URLSearchParams();
    queryParams.append('includeComments', String(includeComments));
    if (includeAttachmentUrls) queryParams.append('includeAttachmentUrls', 'true');

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}?${queryParams.toString()}`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error(`Failed to fetch ticket ${ticketId}:`, response.status, response.statusText);
            return null;
        }

        const res = await response.json();
        return withProvidedReplyState(res);
    } catch (error) {
        console.error(`Error fetching ticket ${ticketId}:`, error);
        return null;
    }
}

// Function to fetch all tickets with optional filtering
export async function getTickets(
    authToken: string,
    filters: { status?: string; sprintId?: string | null; search?: string; skip?: number; take?: number; intakeState?: 'Incoming' | 'Accepted' | 'All'; epicIds?: number[]; backlogOnly?: boolean; assigneeIds?: string[]; statuses?: string[]; tags?: string[]; },
    signal?: AbortSignal,
): Promise<{ tickets: Ticket[]; totalCount: number; } | null> {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.intakeState) queryParams.append('intakeState', filters.intakeState);
    if (filters.backlogOnly) queryParams.set('sprintId', 'backlog');
    filters.epicIds?.forEach(id => queryParams.append('epicIds', String(id)));
    filters.assigneeIds?.forEach(id => queryParams.append('assigneeIds', id));
    filters.statuses?.forEach(status => queryParams.append('statuses', status));
    filters.tags?.forEach(tag => queryParams.append('tags', tag));
    if (!filters.backlogOnly && filters.sprintId) queryParams.set('sprintId', filters.sprintId);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.skip) queryParams.append('skip', filters.skip.toString());
    if (filters.take) queryParams.append('take', filters.take.toString());

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets?${queryParams.toString()}`, {
            signal,
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch tickets:", response.status, response.statusText);
            return null;
        }

        const tickets = await response.json();
        const ticketsList = {
            tickets: Array.isArray(tickets.items) ? tickets.items.map((ticket: Ticket) => withProvidedReplyState(ticket)) : tickets.items,
            totalCount: tickets.totalCount,
        };
        return ticketsList
    } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') return null;
        console.error("Error fetching tickets:", error);
        return null;
    }
}


export async function acceptTicket(authToken: string, ticketId: string, sprintId: string | null = null): Promise<Ticket> {
    return planningRequest('/tickets/' + encodeURIComponent(ticketId) + '/accept', authToken, 'POST', { sprintId });
}

export async function createTicket(
    authToken: string,
    payload: NewTicketRequest
): Promise<ApiResponse<Ticket>> {
    const apiUrl: string = `/tickets`;

    var res = apiPost<ApiResponse<Ticket>>(
        apiUrl, payload, authToken
    )

    return res;
}

export async function updateTicketEpic(
    authToken: string,
    ticketId: string,
    epicId: number | null
): Promise<boolean> {
    try {
        // We reuse the standard PUT /tickets/{id} endpoint as specified in the PRD,
        // sending both the epicId and the epicIdSet flag so the backend knows to process it.
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({
                epicId: epicId,
                epicIdSet: true
            }),
        });

        if (!response.ok) {
            console.error("Failed to update ticket epic:", response.status, response.statusText);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Error updating ticket epic:", error);
        return false;
    }
}

export interface BugSubmissionAcknowledgement { submitted: true; }

export async function submitBug(
    authToken: string,
    bugReport: SubmitBugRequest
): Promise<BugSubmissionAcknowledgement | null> {
    const formData = new FormData();
    formData.append('title', bugReport.title);
    if (bugReport.description) formData.append('description', bugReport.description);
    if (bugReport.pageUrl) formData.append('pageUrl', bugReport.pageUrl);
    if (bugReport.userAgent) formData.append('userAgent', bugReport.userAgent);
    if (bugReport.appVersion) formData.append('appVersion', bugReport.appVersion);
    if (bugReport.os) formData.append('os', bugReport.os);
    if (bugReport.viewport) formData.append('viewport', bugReport.viewport);
    if (bugReport.extra) formData.append('extra', bugReport.extra);

    if (bugReport.files) {
        bugReport.files.forEach(file => {
            formData.append('files', file);
        });
    }

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/submit-bug`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
            body: formData,
        });

        if (!response.ok) {
            const errorBody = await response.text();
            console.error("Failed to submit bug:", response.status, response.statusText, errorBody);
            return null;
        }

        const acknowledgement = await response.json();
        return acknowledgement?.submitted === true ? { submitted: true } : null;
    } catch (error) {
        console.error("Error submitting bug:", error);
        return null;
    }
}

// Function to update an existing ticket
export async function updateTicket(
    authToken: string,
    ticketId: string,
    updates: Partial<NewTicketRequest> // Use a specific update interface if available
): Promise<Ticket | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(updates),
        });

        if (!response.ok) {
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error(`Error updating ticket ${ticketId}:`, error);
        return null;
    }
}

export async function updateTicketAssignee(
    authToken: string,
    ticketId: string,
    assigneeId: string | null
): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/assignee`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ assigneeId }),
        });

        if (!response.ok) {
            console.error("Failed to update ticket assignee:", response.status, response.statusText);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Error updating ticket assignee:", error);
        return false;
    }
}

// Function to delete a ticket by its ID
export async function deleteTicket(
    authToken: string,
    ticketId: string
): Promise<boolean> {
    console.log(`Deleting ticket with ID: ${ticketId}`);
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 200));
    return false;
}

export async function getAssignableUsers(authToken: string): Promise<User[] | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/users/admin-or-developer`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch assignable users:", response.status, response.statusText);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching assignable users:", error);
        return null;
    }
}

export async function getTicketTags(authToken: string, search?: string, take?: number): Promise<Tag[] | null> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    if (take) queryParams.append('take', take.toString());

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/tags?${queryParams.toString()}`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch ticket tags:", response.status, response.statusText);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching ticket tags:", error);
        return null;
    }
}

export async function reorderTickets(authToken: string, payload: ReorderRequest): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/reorder`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            console.error("Failed to reorder tickets:", response.status, response.statusText);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Error reordering tickets:", error);
        return false;
    }
}

export async function closeTicket(
    authToken: string,
    ticketId: string,
    comment: string
): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/close`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ comment }),
        });

        if (!response.ok) {
            console.error("Failed to close ticket:", response.status, response.statusText);
            return false;
        }
        return true;
    } catch (error) {
        console.error("Error closing ticket:", error);
        return false;
    }
}


export async function addTagToTicket(
    authToken: string,
    ticketId: string,
    tagId: string
): Promise<boolean> {

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/tags`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ tagId }),
        });

        if (!response.ok) {
            console.error("Failed to add tag to ticket:", response.status, response.statusText);
            return false;
        }
        return true;
    } catch (error) {
        console.error("Error adding tag to ticket:", error);
        return false;
    }
}



export async function removeTagFromTicket(
    authToken: string,
    ticketId: string,
    tagId: string
): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/tags/${tagId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to remove tag from ticket:", response.status, response.statusText);
            return false;
        }
        return true;

    } catch (error) {
        console.error("Error removing tag from ticket:", error);
        return false;
    }
}

export async function updateTicketStoryPoints(
    authToken: string,
    ticketId: string,
    storyPoints: number | null
): Promise<Ticket | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/story-points`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ storyPoints }),
        });

        if (!response.ok) {
            console.error("Failed to update story points:", response.status, response.statusText);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error("Error updating story points:", error);
        return null;
    }
}

export async function updateTicketPriority(
    authToken: string,
    ticketId: string,
    priority: string
): Promise<Ticket | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/priority`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ priority }),
        });

        if (!response.ok) {
            console.error("Failed to update priority:", response.status, response.statusText);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error("Error updating priority:", error);
        return null;
    }
}

export type BulkTicketUpdate = {
    ticketIds: string[];
    sprintId?: string | null;
    storyPoints?: number | null;
    assigneeId?: string | null;
    statusId?: string;
    epicId?: number | null;
    priority?: string;
};

/**
 * Temporary client-side bulk adapter. This keeps the UI functional with the
 * existing single-ticket endpoints until POST /tickets/bulk-update is added.
 */
export async function bulkUpdateTickets(
    authToken: string,
    updates: BulkTicketUpdate
): Promise<boolean> {
    const operations: Promise<unknown>[] = [];

    updates.ticketIds.forEach(ticketId => {
        if ('assigneeId' in updates) {
            operations.push(updateTicketAssignee(authToken, ticketId, updates.assigneeId ?? null));
        }
        if ('storyPoints' in updates) {
            operations.push(updateTicketStoryPoints(authToken, ticketId, updates.storyPoints ?? null));
        }
        if ('epicId' in updates) {
            operations.push(updateTicketEpic(authToken, ticketId, updates.epicId ?? null));
        }
        if (updates.priority) {
            operations.push(updateTicketPriority(authToken, ticketId, updates.priority));
        }
        if (updates.statusId) {
            operations.push(fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({ statusId: updates.statusId }),
            }).then(response => response.ok));
        }
    });

    if ('sprintId' in updates) {
        operations.push(reorderTickets(authToken, {
            ticketIds: updates.ticketIds,
            targetSprintId: updates.sprintId ?? null,
        }));
    }

    const results = await Promise.allSettled(operations);
    return results.every(result => result.status === 'fulfilled' && result.value !== false && result.value !== null);
}

/** Complete authorized planning scope. Never infer completeness from a capped page. */
export async function getTicketCollection(authToken: string, filters: { intakeState?: 'Incoming' | 'Accepted' | 'All'; sprintId?: string }, signal?: AbortSignal): Promise<{ items: Ticket[]; totalCount: number }> {
    const query = new URLSearchParams();
    if (filters.intakeState) query.set('intakeState', filters.intakeState);
    if (filters.sprintId) query.set('sprintId', filters.sprintId);
    const page = await planningRequest<{ items: Ticket[]; totalCount: number }>('/tickets/collection?' + query, authToken, 'GET', undefined, signal);
    return page && Array.isArray(page.items) ? { ...page, items: page.items.map(ticket => withProvidedReplyState(ticket)) } : page;
}

/**
 * Dismiss the viewer's current "awaiting reply" state. `expectedLatestAt` is the
 * latest comment instant the viewer saw; 409 when it is no longer awaiting or a
 * newer comment arrived.
 */
export async function dismissTicketReply(token: string, id: string, expectedLatestAt?: string): Promise<Ticket> {
    return withProvidedReplyState(await planningRequest<Ticket>('/tickets/' + encodeURIComponent(id) + '/reply-dismissal', token, 'PUT', expectedLatestAt ? { expectedLatestAt } : undefined));
}

/** Undo a dismissal (idempotent). */
export async function undoTicketReplyDismissal(token: string, id: string): Promise<Ticket> {
    return withProvidedReplyState(await planningRequest<Ticket>('/tickets/' + encodeURIComponent(id) + '/reply-dismissal', token, 'DELETE'));
}

// Authoritative variants preserve legacy boolean consumers while allowing targeted reconciliation.
export const updateTicketAssigneeConfirmed = (token: string, id: string, assigneeId: string | null): Promise<Ticket | null> => planningRequest('/tickets/' + encodeURIComponent(id) + '/assignee', token, 'PUT', { assigneeId });
export const updateTicketEpicConfirmed = (token: string, id: string, epicId: number | null): Promise<Ticket | null> => planningRequest('/tickets/' + encodeURIComponent(id), token, 'PUT', { epicId, epicIdSet: true });
export const closeTicketConfirmed = (token: string, id: string, comment: string): Promise<Ticket | null> => planningRequest('/tickets/' + encodeURIComponent(id) + '/close', token, 'POST', { comment });
export const addTagToTicketConfirmed = (token: string, id: string, tagId: string): Promise<Ticket | null> => planningRequest('/tickets/' + encodeURIComponent(id) + '/tags', token, 'POST', { tagId });
export const removeTagFromTicketConfirmed = (token: string, id: string, tagId: string): Promise<Ticket | null> => planningRequest('/tickets/' + encodeURIComponent(id) + '/tags/' + encodeURIComponent(tagId), token, 'DELETE');
export const reorderTicketsConfirmed = (token: string, payload: ReorderRequest): Promise<Ticket[] | null> => planningRequest('/tickets/reorder', token, 'POST', payload);
export interface BulkTicketResult { ticketId: string; ticket: Ticket | null; succeeded: boolean; rejected?: boolean; }
export async function bulkUpdateTicketsConfirmed(token: string, updates: BulkTicketUpdate): Promise<BulkTicketResult[]> {
    const settled = await Promise.allSettled(updates.ticketIds.map(async ticketId => {
        // Board actions own one field; serialize fields if a future caller supplies several.
        let ticket: Ticket | null = null;
        if ('assigneeId' in updates) ticket = await updateTicketAssigneeConfirmed(token, ticketId, updates.assigneeId ?? null);
        if ('epicId' in updates) ticket = await updateTicketEpicConfirmed(token, ticketId, updates.epicId ?? null);
        if ('storyPoints' in updates) ticket = await planningRequest('/tickets/' + encodeURIComponent(ticketId) + '/story-points', token, 'PATCH', { storyPoints: updates.storyPoints ?? null });
        if (updates.statusId) ticket = await planningRequest('/tickets/' + encodeURIComponent(ticketId) + '/status', token, 'PATCH', { statusId: updates.statusId });
        if (updates.priority) ticket = await planningRequest('/tickets/' + encodeURIComponent(ticketId) + '/priority', token, 'PATCH', { priority: updates.priority });
        return ticket;
    }));
    return settled.map((result, index) => ({ ticketId: updates.ticketIds[index], ticket: result.status === 'fulfilled' ? result.value : null, succeeded: result.status === 'fulfilled' && !!result.value, rejected: result.status === 'rejected' && result.reason?.status >= 400 && result.reason?.status < 500 }));
}
export const updateTicketStoryPointsConfirmed = (token: string, id: string, storyPoints: number | null): Promise<Ticket | null> => planningRequest('/tickets/' + encodeURIComponent(id) + '/story-points', token, 'PATCH', { storyPoints });
export const updateTicketPriorityConfirmed = (token: string, id: string, priority: string): Promise<Ticket | null> => planningRequest('/tickets/' + encodeURIComponent(id) + '/priority', token, 'PATCH', { priority });
